import BaseModel from './BaseModel'
import {action, makeObservable, observable, runInAction} from 'mobx'
import Game from './Game'
import {post} from '../common/requester'
import GamesQuery from "./GamesQuery";
import GameMembersQuery from "./GameMembersQuery";
import GameTeam from "./GameTeam";
import Competition from "./Competition";
import CompetitionsQuery from "./CompetitionsQuery";
import Award from "./Award";

export default class Tournament extends BaseModel {
  type = 'manager';

  constructor(store, type) {
    super(store);
    makeObservable(this, {
      type: observable,
      company_id: observable,

      competition: observable,
      competition_details: observable,
      competitions: observable,
      competitionsQuery: observable,

      games: observable,
      game: observable,
      gamesQuery: observable,

      members: observable,
      membersQuery: observable,

      gameTeam: observable,

      award: observable,

      competitionBillingLoaded: observable,

      divisions: observable,
      groups: observable,

      getTournamentDivisions: action,
      saveTournamentDivision: action,
      deleteTournamentDivision: action,

      getTournamentGroups: action,
      saveTournamentGroup: action,
      deleteTournamentGroup: action,

      saveGame: action,
      searchGames: action,
      getGame: action,
      getGameTeam: action,
      getMembersForGameTeam: action,
      addMemberToTeam: action,
      deleteMemberFromTeam: action,
      saveGameTeam: action,
      getCompetitions: action,
      resetCompetition: action,
      getCompetition: action,
      getCompetitionDetails: action,
      resetGameTeam: action,
      searchTournamentsForFilter: action,
      resetGame: action,
      getTeamsForTournament: action,
      getAwards: action,
      getAwardTypes: action,
      createAward: action,
      getGameParticipants: action,
      saveGameParticipants: action,
      getTeamsForCompany: action,
      getCompanyMembersDropdown: action,

      getCompetitionBilling: action,
      generateCompetitionBilling: action,
      informCompetitionBill: action,
      informCompetitionBills: action,
      deleteCompetitionBilling: action,

      tournamentCompanies: observable,
      getTournamentCompanies: action,
      inviteCompanyToTournament: action,
      acceptTournamentInvitation: action,
      declineTournamentInvitation: action,
      removeCompanyFromTournament: action,
      addTeamToTournament: action,
      removeTeamFromTournament: action,
      setTournamentTeamDivision: action,
      setTournamentTeamGroup: action,
      searchOwnedTournamentsForGame: action,
      getGameCompaniesForTournament: action,
      getTournamentTeamsForGame: action,
      getTournamentPlayersForGame: action
    })
    this.type = type
  }

  company_id = 0
  competition = new Competition(this.store)
  competition_details = {}
  competitions = []
  games = []
  game = new Game()
  gamesQuery = new GamesQuery()
  members = []
  membersQuery = new GameMembersQuery()
  gameTeam = new GameTeam()
  competitionsQuery = new CompetitionsQuery()
  award = new Award(this)

  competitionBillingLoaded = false

  tournamentCompanies = []
  divisions = []
  groups = []

  async saveGame() {
    this.game.setProperty('editor_company_id', this.company_id)

    if (this.game.isBaseValid) {
      const ret = await post('game/save_game', this.game.toJson()).catch(error => console.error(error))
      if (ret && ret.result === 0) {
        this.resetGame()
      }
      return ret
    }
    return null
  }

  async searchGames() {
    const args = {type: this.type, query: {...{company_id: this.company_id}, ...this.gamesQuery.toJson()}}
    const ret = await post('game/search_games', args).catch(error => console.error(error))
    if (ret && ret.result === 0) {
      this.setProperty('games', ret.games.items)
      this.gamesQuery.setProperty('count', ret.games.count)
      this.gamesQuery.setProperty('page_count', ret.games.page_count)
      this.gamesQuery.setProperty('page_size', ret.games.page_size)
      this.gamesQuery.setProperty('page_number', ret.games.page_number)
    }
  }

  async getGame(game_id) {
    const ret = await post('game/get_game_details', {game_id: game_id}).catch(error => console.error(error))
    if (ret && ret.result === 0) {
      runInAction(() => {
        this.game = new Game(this.store);
        this.game.fromJson(ret.game);
      });
    }
  }

  async getGameTeam(game_team_id) {
    const ret = await post('game/get_game_team', {game_team_id: game_team_id}).catch(error => console.error(error))
    if (ret && ret.result === 0) {
      this.gameTeam.fromJson(ret.game_team)
    }
  }

  async getMembersForGameTeam(game_team_id) {
    const ret = await post('game/get_members_for_game_team', {
      game_team_id: game_team_id,
      title: this.membersQuery.title,
      year_born: this.membersQuery.year_born
    }).catch(error => console.error(error))
    if (ret && ret.result === 0) {
      this.setProperty('members', ret.members)
    }
  }

  async addMemberToTeam(game_team_id, member_id) {
    const ret = await post('game/add_member_to_team', {
      game_team_id: game_team_id,
      member_id: member_id
    }).catch(error => console.error(error))
    if (ret && ret.result === 0) {
      this.getMembersForGameTeam(game_team_id)
      this.getGameTeam(game_team_id)
    }
  }

  async deleteMemberFromTeam(game_team_id, member_id) {
    const ret = await post('game/delete_member_from_team', {
      game_team_id: game_team_id,
      member_id: member_id
    }).catch(error => console.error(error))
    if (ret && ret.result === 0) {
      this.getMembersForGameTeam(game_team_id)
      this.getGameTeam(game_team_id)
    }
  }

  async saveGameTeam() {
    return post('game/save_game_team', {
      game_team_id: this.gameTeam.id,
      name: this.gameTeam.name,
      tournament_id: this.gameTeam.tournament_id,
      company_id: this.company_id
    })
  }

  async getCompetitions() {
    const ret = await post('game/get_tournaments', {
      company_id: this.company_id,
      query: this.competitionsQuery.toJson()
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      this.setProperty('competitions', ret.tournaments.items)
      const {items, ...queryData} = ret.tournaments;
      this.competitionsQuery.fromJson(queryData);
    }
  }

  resetCompetition() {
    this.competition = new Competition(this.store)
    this.competition.setProperty('company_id', this.company_id)
    this.competitionBillingLoaded = false
  }

  async getCompetition(competition_id) {
    const ret = await post('game/get_tournament', {
      company_id: this.company_id,
      tournament_id: competition_id
    }).catch(error => console.error(error))
    if (ret && ret.result === 0) {
      runInAction(() => {
        this.competition = new Competition(this.store)
        this.competition.fromJson(ret.tournament)
        this.setProperty('divisions', ret.tournament?.divisions || [])
      })
    }
  }

  async getCompetitionDetails(competition_id) {
    const ret = await post('game/get_tournament_details', {
      company_id: this.company_id,
      tournament_id: competition_id
    }).catch(error => console.error(error))
    if (ret && ret.result === 0) {
      runInAction(() => {
        this.competition_details = ret.tournament
      })
    }
  }

  resetGameTeam() {
    this.gameTeam = new GameTeam()
  }

  async searchTournamentsForFilter(name = null, tournament_id = null) {
    const res = await post('game/search_tournaments_for_filter', {
      company_id: this.company_id,
      name: name,
      tournament_id: tournament_id
    }).catch((err) => console.error('--search tournaments error', err))
    if (res) {
      return res.tournaments
    }
  }

  resetGame() {
    this.game = new Game()
    this.game.setProperty('company_id', this.company_id)
  }

  async getTeamsForTournament(tournament_id) {
    return post('game/get_teams_for_tournament', {
      company_id: this.company_id,
      tournament_id: tournament_id
    }).catch(error => console.error(error))
  }

  async getGuestCompanies(tournament_id) {
    return post('game/get_guest_companies', {
      company_id: this.company_id,
      tournament_id: tournament_id
    }).catch(error => console.log(error))
  }

  async getAwardTypes({sport_id, scope, include_common = true} = {}) {
    this.award.setTypeFilter({sport_id, scope, include_common});
    return this.award.getAwardTypes();
  }

  async getAwards(filters = {}) {
    return this.award.listAwards(filters);
  }

  async createAward(payload = {}) {
    return this.award.createAward({tournament_id: this.tournament_id, ...payload});
  }

  async getGameParticipants(game_id, game_team_id, company_id) {
    return await post('game/get_game_participants', {
      game_id,
      game_team_id,
      company_id
    }).catch(error => console.error(error))
  }

  async saveGameParticipants(game_id, game_team_id, participants, company_id) {
    return await post('game/save_game_participants', {
      game_id,
      game_team_id,
      participants,
      company_id
    }).catch(error => console.error(error))
  }

  async getTeamsForCompany(company_id) {
    return await post('game/get_teams_for_company', {company_id}).catch(error => console.error(error))
  }

  async getCompanyMembersDropdown(company_id) {
    return await post('game/get_company_members_dropdown', { company_id })
      .catch(error => console.error(error));
  }

  // ---------------------------------
  // TOURNAMENT BILLING
  // ---------------------------------

  async getCompetitionBilling(tournament_id) {
    const ret = await post('game/get_tournament_billing', {
      company_id: this.company_id,
      tournament_id: tournament_id
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      runInAction(() => {
        this.competition = new Competition(this.store)
        this.competition.fromJson(ret.tournament)
        this.competitionBillingLoaded = true
      })
    }
    return ret
  }

  async generateCompetitionBilling({tournament_id, tournament_team_id, company_user_ids = []}) {
    return await post('game/generate_tournament_billing', {
      company_id: this.company_id,
      tournament_id,
      tournament_team_id,
      company_user_ids
    }).catch(error => console.error(error))
  }

  async informCompetitionBill({tournament_id, tournament_team_id, company_user_id, bill_id = null}) {
    return await post('game/inform_tournament_bill', {
      company_id: this.company_id,
      tournament_id,
      tournament_team_id,
      company_user_id,
      bill_id
    }).catch(error => console.error(error))
  }

  async informCompetitionBills({tournament_id, tournament_team_id, company_user_ids = []}) {
    return await post('game/inform_tournament_bills', {
      company_id: this.company_id,
      tournament_id,
      tournament_team_id,
      company_user_ids
    }).catch(error => console.error(error))
  }

  async deleteCompetitionBilling({tournament_id, tournament_team_id, company_user_id, bill_id}) {
    return await post('manager/billing/delete', {company_id: this.company_id, billing_id: bill_id})
        .catch(error => console.error(error))
  }


  async getTournamentCompanies(tournament_id) {
    const ret = await post('game/get_tournament_companies', {
      tournament_id,
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      this.setProperty('tournamentCompanies', ret.companies || [])
    }

    return ret
  }

  async inviteCompanyToTournament(tournament_id, company_id) {
    const ret = await post('game/invite_company_to_tournament', {
      tournament_id,
      company_id,
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      await this.getTournamentCompanies(tournament_id)
    }

    return ret
  }

  async acceptTournamentInvitation(tournament_id, company_id) {
    const ret = await post('game/accept_tournament_invitation', {
      tournament_id,
      company_id,
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      await this.getTournamentCompanies(tournament_id)
      await this.getCompetition(tournament_id)
    }

    return ret
  }

  async declineTournamentInvitation(tournament_id, company_id) {
    const ret = await post('game/decline_tournament_invitation', {
      tournament_id,
      company_id,
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      await this.getTournamentCompanies(tournament_id)
      await this.getCompetition(tournament_id)
    }

    return ret
  }

  async removeCompanyFromTournament(tournament_id, company_id) {
    const ret = await post('game/remove_company_from_tournament', {
      tournament_id,
      company_id,
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      await this.getTournamentCompanies(tournament_id)
      await this.getCompetition(tournament_id)
    }

    return ret
  }

  async addTeamToTournament(tournament_id, game_team_id) {
    const ret = await post('game/add_team_to_tournament', {
      tournament_id,
      game_team_id,
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      await this.getCompetition(tournament_id)
    }

    return ret
  }

  async removeTeamFromTournament(tournament_id, game_team_id) {
    const ret = await post('game/remove_team_from_tournament', {
      tournament_id,
      game_team_id,
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      await this.getCompetition(tournament_id)
    }

    return ret
  }

  async getTournamentDivisions(tournament_id) {
    const ret = await post('game/get_tournament_divisions', {
      tournament_id
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      this.setProperty('divisions', ret.divisions || [])
    }

    return ret
  }

  async saveTournamentDivision(data) {
    const ret = await post('game/save_tournament_division', data)
        .catch(error => console.error(error))

    if (ret && ret.result === 0) {
      await this.getTournamentDivisions(data.tournament_id)
      if (this.competition?.id === Number(data.tournament_id)) {
        await this.getCompetition(data.tournament_id)
      }
    }

    return ret
  }

  async deleteTournamentDivision(id, tournament_id) {
    const ret = await post('game/delete_tournament_division', {
      id
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      await this.getTournamentDivisions(tournament_id)
      if (this.competition?.id === Number(tournament_id)) {
        await this.getCompetition(tournament_id)
      }
    }

    return ret
  }

  async getTournamentGroups(division_id) {
    const ret = await post('game/get_tournament_groups', {
      division_id
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      this.setProperty('groups', ret.groups || [])
    }

    return ret
  }

  async saveTournamentGroup(data) {
    const ret = await post('game/save_tournament_group', data)
        .catch(error => console.error(error))

    if (ret && ret.result === 0) {
      await this.getTournamentGroups(data.division_id)
      await this.getTournamentDivisions(data.tournament_id)
      if (this.competition?.id === Number(data.tournament_id)) {
        await this.getCompetition(data.tournament_id)
      }
    }

    return ret
  }

  async deleteTournamentGroup(id, division_id, tournament_id) {
    const ret = await post('game/delete_tournament_group', {
      id
    }).catch(error => console.error(error))

    if (ret && ret.result === 0) {
      await this.getTournamentGroups(division_id)
      await this.getTournamentDivisions(tournament_id)
      if (this.competition?.id === Number(tournament_id)) {
        await this.getCompetition(tournament_id)
      }
    }

    return ret
  }

  async setTournamentTeamDivision(tournament_team_id, division_id, tournament_id) {
    const ret = await post('game/set_tournament_team_division', {
      tournament_team_id,
      division_id,
    }).catch(error => console.error(error))

    if (ret && ret.result === 0 && tournament_id) {
      await this.getCompetition(tournament_id)
    }

    return ret
  }

  async setTournamentTeamGroup(tournament_team_id, group_id, tournament_id) {
    const ret = await post('game/set_tournament_team_group', {
      company_id: this.company_id,
      tournament_team_id,
      group_id,
    }).catch(error => console.error(error))

    if (ret && ret.result === 0 && tournament_id) {
      await this.getCompetition(tournament_id)
    }

    return ret
  }

  async searchOwnedTournamentsForGame(name = null) {
    const res = await post('game/search_owned_tournaments_for_game', {
      company_id: this.company_id,
      name,
    }).catch((err) => console.error('--search owned tournaments error', err));

    if (res) return res.tournaments;
    return [];
  }

  async getGameCompaniesForTournament(tournament_id) {
    return await post('game/get_game_companies_for_tournament', {
      company_id: this.company_id,
      tournament_id,
    }).catch(error => console.error(error));
  }

  async getTournamentTeamsForGame({tournament_id, company_id, division_id = null, group_id = null}) {
    return await post('game/get_tournament_teams_for_game', {
      editor_company_id: this.company_id,
      tournament_id,
      company_id,
      division_id,
      group_id
    }).catch(error => console.error(error));
  }

  async getTournamentPlayersForGame({tournament_id, company_id, division_id = null, group_id = null}) {
    return await post('game/get_tournament_players_for_game', {
      editor_company_id: this.company_id,
      tournament_id,
      company_id,
      division_id,
      group_id
    }).catch(error => console.error(error));
  }
}